import React, { useMemo } from 'react';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { bool, func, number, object, string, array } from 'prop-types';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { ActionCtas } from '@fiverr-private/gig_page_nav';
import { Container, Stack } from '@fiverr-private/layout_components';
import { CustomerProtectionBanner } from '@fiverr-private/gig_page_banners';
import {
    useShouldShowLoyaltyBanner,
} from '@fiverr-private/loyalty_indication';
import StudioBox from '../../StudioBox';
import PackagesTabs from '../../PackagesTabs';
import CollectWrapper from '../../TopNav/CollectWrapper';
import ReportBtn from '../../TopNav/ReportGig/ReportBtn';
import PayLaterMessage from '../../PayLaterMessage/PayLaterMessage';
import { getPackageById, getSelectedPackageId } from '../../../reducers/packages';
import { ContactHourlySellerCard } from '../../ContactHourlySellerCard';
import { shouldRenderContactSeller } from '../../ContactSeller/utils';
import { ContactSeller } from '../../ContactSeller/ContactSeller';
import { LoyaltyBanner } from '../../LoyaltyBanner/LoyaltyBanner';
import { isInLynxesCtaReworkTest } from '../../../utils/experiments/isInLynxesCtaRework';
import { SidebarHighlights } from './SidebarHighlights';

import {
    contactWrapperClass,
    contactHourlySellerWrapperClass,
    compactBannersWrapperClass,
} from './SidebarContent.ve.css';

const LOYALTY_BANNER_TYPE = 'gig_page';

const SidebarContent = ({
    customOrder,
    useStickyLayout,
    className = '',
    setRef = noop,
    seller = {},
    currentPrice,
    rollouts = {},
    packageList,
}) => {
    const { general, loyaltyIndication = {}, currencyConverter, isBusinessLayout } = useGigPageContext();
    const { isStudio } = general;
    const gigInfo = {
        gigPriceInUSDCents: currentPrice || 0,
        isProSeller: seller?.isPro,
        sellerName: seller?.user?.profile?.displayName || '',
    };

    const shouldShowLoyaltyIndicationHookCheck = useShouldShowLoyaltyBanner({
        gigInfo,
        loyaltyIndicationData: loyaltyIndication,
        type: LOYALTY_BANNER_TYPE,
    });

    const shouldShowLoyaltyIndication = Boolean(shouldShowLoyaltyIndicationHookCheck && loyaltyIndication?.points);

    const shouldShowHourlyIndications = !!seller?.hourlyRate;

    const currency = currencyConverter.getCurrency();

    const shouldShowCustomerProtectionBanner = Boolean(useStickyLayout && isBusinessLayout && seller?.isPro);

    const isInLynxesCtaReworkExperiment = isInLynxesCtaReworkTest(packageList || []);
    const shouldDisplayContactSeller = useMemo(
        () =>
            shouldRenderContactSeller({
                general,
                useStickyLayout,
            }) && !isInLynxesCtaReworkExperiment,
        [general, useStickyLayout, isInLynxesCtaReworkExperiment]
    );

    return (
        <aside className={classNames(className, 'sidebar-content')} ref={setRef}>
            <Container paddingBottom="3" display={{ default: 'none', md: 'block' }}>
                <ActionCtas ReportGigButtonWrapper={ReportBtn} CollectionCta={CollectWrapper} />
            </Container>

            {isStudio && <StudioBox />}

            <PackagesTabs />
            {shouldShowHourlyIndications && (
                <ContactHourlySellerCard className={classNames(contactHourlySellerWrapperClass, 'contact-hourly-seller-card')} rollouts={rollouts} />
            )}

            {!shouldShowHourlyIndications && (
                <>
                    <Stack
                        order={55}
                        className={contactWrapperClass}
                        borderRadius="xl"
                        direction="column"
                        wrap="nowrap"
                    >
                        {shouldDisplayContactSeller && <ContactSeller customOrder={customOrder} />}
                        <SidebarHighlights shouldUseStickyLayout={useStickyLayout} />
                    </Stack>
                    {shouldShowLoyaltyIndication && (
                <LoyaltyBanner
                    currency={currency}
                    seller={seller}
                    currentPrice={currentPrice}
                    containerStyles={
                        shouldShowCustomerProtectionBanner
                            ? {
                                  borderBottomLeftRadius: 'none',
                                  borderBottomRightRadius: 'none',
                              }
                            : undefined
                    }
                />
            )}

                    <PayLaterMessage />

                    <Container
                        hidden={{
                            default: true,
                            md: !shouldShowCustomerProtectionBanner,
                        }}
                    >
                        <CustomerProtectionBanner />
                    </Container>
                </>
            )}

            {shouldShowHourlyIndications && (
                <>
                    <Banners
                        shouldShowCustomerProtectionBanner={shouldShowCustomerProtectionBanner}
                        shouldShowLoyaltyIndication={shouldShowLoyaltyIndication}
                        currentPrice={currentPrice}
                    />
                    <PayLaterMessage />
                </>
            )}
        </aside>
    );
};

SidebarContent.propTypes = {
    customOrder: object,
    setRef: func,
    useStickyLayout: bool,
    seller: object,
    className: string,
    currentPrice: number,
    rollouts: object,
    packageList: array,
};

export const Banners = ({ currentPrice, shouldShowCustomerProtectionBanner, shouldShowLoyaltyIndication }) => {
    const { currencyConverter, seller } = useGigPageContext();
    const currency = currencyConverter.getCurrency();

    return (
        <Stack
            hidden={{
                default: true,
                md: false,
            }}
            direction="column"
            gap="0"
            className={compactBannersWrapperClass}
        >
            {shouldShowLoyaltyIndication && (
                <LoyaltyBanner
                    currency={currency}
                    seller={seller}
                    currentPrice={currentPrice}
                    isCollapsible
                    containerStyles={
                        shouldShowCustomerProtectionBanner
                            ? {
                                  borderBottomLeftRadius: 'none',
                                  borderBottomRightRadius: 'none',
                              }
                            : undefined
                    }
                />
            )}
            {shouldShowCustomerProtectionBanner && (
                <CustomerProtectionBanner
                    containerStyles={
                        shouldShowLoyaltyIndication
                            ? {
                                  borderTopLeftRadius: 'none',
                                  borderTopRightRadius: 'none',
                                  borderTop: 'none',
                                  style: { borderTop: 'none' },
                              }
                            : undefined
                    }
                    isCollapsible
                />
            )}
        </Stack>
    );
};

Banners.propTypes = {
    currentPrice: number,
    shouldShowCustomerProtectionBanner: bool,
    shouldShowLoyaltyIndication: bool,
};

const mapStateToProps = (state) => {
    const { packages } = state;
    const packageId = getSelectedPackageId(state);
    const { currentPrice } = getPackageById(packages, packageId) || {};

    return {
        currentPrice,
        packageList: packages?.packageList || [],
    };
};

export default connect(mapStateToProps)(SidebarContent);